/**
 * Tab "Pay Info" của màn Contract Detail — mirror Access `frmProcurementQryDetailsPTab1`
 * (variant P) / `frmProcurementQryDetailsNPETab1` (variant NPE):
 *
 *   ┌ SUMMARY ──────────────────┐┌ REQUEST PAYMENT ───────────────────────────┐
 *   │ "Received and Paid"       ││ For Outright [Write data]  For PTBF [Write │
 *   │ grid GoodsAlc             ││ data PTBF]; Futures (Latest) | Requested FX│
 *   │                           ││ grid Paydata                               │
 *   └───────────────────────────┘└────────────────────────────────────────────┘
 *   ┌ INVOICE ─────────────────────────────────────────────────────────────────┐
 *   │ Invoices: For TLC [Write data]  ("For Sub" Access ẩn → không render)      │
 *   │ grid Invoice                                                              │
 *   └──────────────────────────────────────────────────────────────────────────┘
 *
 * Cột datasheet theo ColumnOrder/TabIndex Access — P và NPE KHÁC nhau (GoodsAlc,
 * Paydata); Invoice giống nhau. Banner màu tint theo tone TLC (không copy màu Access).
 */
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import { useEffect, useMemo } from 'react'

import {
  useListContractGoodsAlcQuery,
  useListContractInvoiceQuery,
  useListContractPaydataQuery,
} from '../../store/api/procurementApi'
import ProcurementDatasheet, {
  type DatasheetColumn,
  type DatasheetRow,
} from './ProcurementDatasheet'
import type { ContractVariant } from './ContractDetailHeader'

// ---- Cột GoodsAlc (Access ProcurementPayQryGoodsAlc_P — ColumnOrder; id hidden) ----
const GOODS_ALC_COLUMNS_P: DatasheetColumn[] = [
  { field: 'm_date', header: 'Date', kind: 'date', width: 80, freeze: true },
  { field: 'm_truck_no', header: 'TruckNo', kind: 'text', width: 80 },
  { field: 'truck_id', header: 'TruckID', kind: 'text', width: 62 },
  { field: 'truck_status', header: 'TruckStatus', kind: 'text', width: 90, filter: 'checkboxes' },
  { field: 'estimated_quantity', header: 'Est.Quant(Kg)', kind: 'int', width: 88, sum: true },
  { field: 'allocating_quantity', header: 'Alc.Quant(Kg)', kind: 'int', width: 88, sum: true },
  { field: 'requested_provi', header: 'Req.Provi', kind: 'int', width: 78, sum: true },
  { field: 'to_be_requested_provi', header: 'Pending.Provisional', kind: 'int', width: 118, sum: true },
  { field: 'requested_fix', header: 'Req.Fixation/Outright', kind: 'int', width: 128, sum: true },
  { field: 'to_be_requested_fix', header: 'Pending.Fixation/Outright', kind: 'int', width: 148, sum: true },
  { field: 'to_warehouse', header: 'To Warehouse', kind: 'text', width: 116, filter: 'checkboxes' },
  { field: 'receipt_no', header: 'ReceiptNo', kind: 'text', width: 150 },
]

// ---- Cột GoodsAlc (Access ProcurementPayQryGoodsAlc_NPE — ColumnOrder datasheet
//      frmProcurementQryDetailsNPETab1_GoodsAlc: To Warehouse ở #8, KHÔNG phải #2) ----
const GOODS_ALC_COLUMNS_NPE: DatasheetColumn[] = [
  { field: 'id', header: 'ID', kind: 'id', width: 60, freeze: true },
  { field: 'm_date', header: 'M Date', kind: 'date', width: 82 },
  { field: 'm_truck_no', header: 'M Truck No', kind: 'text', width: 84 },
  { field: 'truck_id', header: 'Truck ID', kind: 'text', width: 66 },
  { field: 'estimated_quantity', header: 'Estimated Quantity', kind: 'int', width: 112, sum: true },
  { field: 'allocating_quantity', header: 'Allocating (Kg)', kind: 'int', width: 96, sum: true },
  { field: 'requested_qry', header: 'Requested Qty', kind: 'int', width: 92, sum: true },
  { field: 'to_warehouse', header: 'To Warehouse', kind: 'text', width: 116, filter: 'checkboxes' },
  { field: 'receipt_no', header: 'ReceiptNo', kind: 'text', width: 150 },
]

// ---- Cột Paydata (Access ProcurementPayQryPayData_P — ColumnOrder) ----
const PAYDATA_COLUMNS_P: DatasheetColumn[] = [
  { field: 'paydata_p_id', header: 'ID', kind: 'id', width: 58, freeze: true },
  { field: 'payment_type', header: 'Payment Type', kind: 'text', width: 96, filter: 'checkboxes' },
  { field: 'xntt_no', header: 'XNTT No', kind: 'text', width: 96 },
  { field: 'truck_id', header: 'Truck ID', kind: 'text', width: 66 },
  { field: 'bank', header: 'Bank', kind: 'text', width: 84, filter: 'checkboxes' },
  { field: 'quantity', header: 'Quantity', kind: 'int', width: 84, sum: true },
  { field: 'payment_date', header: 'Payment Date', kind: 'date', width: 92 },
  { field: 'requested_vnd', header: 'VND - Requested', kind: 'int', width: 108, sum: true },
  { field: 'requested_usd', header: 'USD - Requested', kind: 'num3', width: 108, sum: true },
  { field: 'requested_fx', header: 'FX - Requested', kind: 'num2', width: 100 },
  { field: 'advanced', header: 'Advanced', kind: 'num3', width: 96, sum: true },
  { field: 'vat_amount_vnd', header: 'VAT Amount VND', kind: 'int', width: 108, sum: true },
  { field: 'paid_usd', header: 'USD - Actual Payment', kind: 'num3', width: 124, sum: true },
  { field: 'paid_vnd', header: 'VND - Actual Payment', kind: 'int', width: 124, sum: true },
  { field: 'reconcile', header: 'Reconcile', kind: 'num2', width: 120, sum: true },
  { field: 'invoice_no', header: 'Invoice No', kind: 'text', width: 150 },
  { field: 'note', header: 'Note - Others', kind: 'text', width: 110 },
  { field: 'stoploss', header: 'Stoploss', kind: 'num2', width: 82 },
]

// ---- Cột Paydata (Access ProcurementPayQryPayData_NPE — TabIndex order) ----
const PAYDATA_COLUMNS_NPE: DatasheetColumn[] = [
  { field: 'paydata_p_id', header: 'ID', kind: 'id', width: 58, freeze: true },
  { field: 'payment_type', header: 'Payment Type', kind: 'text', width: 96, filter: 'checkboxes' },
  { field: 'xntt_no', header: 'XNTT No', kind: 'text', width: 96 },
  { field: 'truck_id', header: 'Truck ID', kind: 'text', width: 66 },
  { field: 'bank', header: 'Request - Bank', kind: 'text', width: 104, filter: 'checkboxes' },
  { field: 'quantity', header: 'Request - Quantity', kind: 'int', width: 112, sum: true },
  { field: 'payment_date', header: 'Payment Date', kind: 'date', width: 92 },
  { field: 'requested_vnd', header: 'Requested VND', kind: 'int', width: 110, sum: true },
  { field: 'vat_amount_vnd', header: 'VAT Amount VND', kind: 'int', width: 108, sum: true },
  { field: 'paid_usd', header: 'Actual Payment - USD', kind: 'int', width: 124, sum: true },
  { field: 'fx', header: 'Actual Payment - FX', kind: 'num3', width: 118 },
  { field: 'paid_vnd', header: 'Actual Payment - VND', kind: 'int', width: 124, sum: true },
  { field: 'reconcile', header: 'Reconcile', kind: 'num2', width: 120, sum: true },
  { field: 'invoice_no', header: 'Invoice No', kind: 'text', width: 150 },
  { field: 'note', header: 'Others - Note', kind: 'text', width: 110 },
  { field: 'stoploss', header: 'Request - Stoploss', kind: 'num2', width: 112 },
]

// ---- Cột Invoice (Access ProcurementPayQryInvoice_P/_NPE — giống nhau) ----
const INVOICE_COLUMNS: DatasheetColumn[] = [
  { field: 'invoice_id', header: 'ID', kind: 'id', width: 80 },
  { field: 'warehouse', header: 'Warehouse', kind: 'text', width: 170, filter: 'checkboxes' },
  { field: 'invoice_no', header: 'Invoice No', kind: 'text', width: 140, freeze: true },
  { field: 'invoice_date', header: 'Invoice Date', kind: 'date', width: 110 },
  { field: 'quantity', header: 'Quantity', kind: 'num2', width: 130, sum: true },
  { field: 'price_vnd_kg', header: 'Price (VND/KG)', kind: 'num2', width: 130 },
  { field: 'total_amount', header: 'Total Amount', kind: 'num2', width: 130, sum: true },
  { field: 'vat_amount', header: 'VAT Amount', kind: 'num2', width: 130, sum: true },
  { field: 'total_amount_after_tax', header: 'Invoice Amount (after tax)', kind: 'num2', width: 160, sum: true },
]


const actionBtnSx = {
  minWidth: 0,
  px: 1.25,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none' as const,
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

const fmtNum2 = (v: number | null | undefined): string =>
  v == null || !Number.isFinite(Number(v))
    ? ''
    : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const toRows = (items: readonly Record<string, unknown>[] | undefined, key: string): DatasheetRow[] =>
  (items ?? []).map((it, idx) => ({ ...it, id: `${it[key] ?? idx}` }))

interface Props {
  variant: ContractVariant
  contractNo: string
  /** Access contract_type ('Outright' vs Differential/PTBF) — chọn action nút Write. */
  contractType: string
  /** Futures (Latest) — Access DLookup v_pos_dashboard_pnlmarket (header endpoint). */
  fixLevel: number | null
  /** Default cho ô Requested FX — Access fixed_fx nếu ≠ 0. */
  fixedFx: number | null
  /** Req.FX — controlled + persist theo user (/user-logaction) ở parent. */
  reqFx: string
  onReqFxChange: (v: string) => void
  onWriteOutright: (requestedFx: string) => void
  onWritePTBF: (requestedFx: string) => void
  onWriteInvoice: () => void
  /** Contract đã Forced Close → disable các nút Write (mirror guard runtime ở parent). */
  disabled?: boolean
}

export default function ContractDetailPayInfo({
  variant,
  contractNo,
  contractType,
  fixLevel,
  fixedFx,
  reqFx,
  onReqFxChange,
  onWriteOutright,
  onWritePTBF,
  onWriteInvoice,
  disabled = false,
}: Props) {
  const goodsAlc = useListContractGoodsAlcQuery({ contractNo })
  const paydata = useListContractPaydataQuery({ contractNo })
  const invoice = useListContractInvoiceQuery({ contractNo })

  // Ô "Requested FX" Access = textbox unbound; prefill fixed_fx CHỈ khi đang rỗng
  // (giữ giá trị user đã lưu qua /user-logaction, không ghi đè mỗi lần đổi contract).
  const requestedFx = reqFx
  useEffect(() => {
    if (!reqFx && fixedFx) onReqFxChange(fmtNum2(fixedFx))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractNo, fixedFx])

  const goodsAlcRows = useMemo(() => toRows(goodsAlc.data?.items, 'id'), [goodsAlc.data?.items])
  const paydataRows = useMemo(() => toRows(paydata.data?.items, 'paydata_p_id'), [paydata.data?.items])
  const invoiceRows = useMemo(() => toRows(invoice.data?.items, 'invoice_id'), [invoice.data?.items])

  const goodsAlcColumns = variant === 'p' ? GOODS_ALC_COLUMNS_P : GOODS_ALC_COLUMNS_NPE
  const paydataColumns = variant === 'p' ? PAYDATA_COLUMNS_P : PAYDATA_COLUMNS_NPE

  // 1 nút Write data — action theo loại hợp đồng: NPE + P-Outright → Outright path;
  // P-Differential → PTBF path (mirror handleWriteOutright/handleWritePTBF ở parent).
  const isOutright = variant === 'npe' || contractType === 'Outright'
  const writeLabel = variant === 'npe' ? 'Write data' : isOutright ? 'Write data (Outright)' : 'Write data (PTBF)'
  const handleWrite = () => (isOutright ? onWriteOutright : onWritePTBF)(requestedFx)

  return (
    <Box sx={{
      // Desktop (lg): flex-fill + scroll nội bộ (header pin ở trên). Mobile/tablet:
      // height tự nhiên (flex none, overflow visible) để cả 3 datasheet đổ đầy nội
      // dung + cuộn CHUNG với header ở root view — tránh khe scroll tí hon lồng nhau.
      flex: { xs: 'none', lg: 1 },
      minHeight: { lg: 0 },
      overflowY: { xs: 'visible', lg: 'auto' },
      display: 'flex', flexDirection: 'column', gap: 1, pr: 0.5,
    }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1 }}>
        {/* ---- SUMMARY (trái): GoodsAlc — fieldset khung + legend trên viền ---- */}
        <Box
          component="fieldset"
          sx={(theme) => ({
            minWidth: 0,
            m: 0,
            px: 0.75,
            pt: 0.5,
            pb: 0.5,
            border: '1px solid',
            borderColor: alpha(theme.palette.primary.main, 0.4),
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
          })}
        >
          <Box
            component="legend"
            sx={{
              px: 0.5,
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: 0.3,
              color: 'primary.dark',
              width: 'auto',
              mb: 0,
            }}
          >
            SUMMARY — Received and Paid
          </Box>
          <ProcurementDatasheet
            storageKey={`procurement.contract-detail.goodsalc.${variant}`}
            columns={goodsAlcColumns}
            rows={goodsAlcRows}
            isLoading={goodsAlc.isLoading}
            isFetching={goodsAlc.isFetching}
            error={goodsAlc.error}
            primaryField={variant === 'p' ? 'm_date' : 'id'}
            searchPlaceholder="Search goods allocation…"
            errorTitle="Could not load goods allocation"
            emptyTitle="No goods allocation"
            emptyDescription="No rows for the selected contract."
            maxBodyHeight={180}
          />
        </Box>

        {/* ---- REQUEST PAYMENT (phải): nút Write + Futures/FX + Paydata ---- */}
        <Box
          component="fieldset"
          sx={(theme) => ({
            minWidth: 0,
            m: 0,
            px: 0.75,
            pt: 0.5,
            pb: 0.5,
            border: '1px solid',
            borderColor: alpha(theme.palette.secondary.main, 0.4),
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
          })}
        >
          <Box
            component="legend"
            sx={{
              px: 0.5,
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: 0.3,
              color: 'secondary.dark',
              width: 'auto',
              mb: 0,
            }}
          >
            REQUEST PAYMENT
          </Box>
          <ProcurementDatasheet
            storageKey={`procurement.contract-detail.paydata.${variant}`}
            columns={paydataColumns}
            rows={paydataRows}
            isLoading={paydata.isLoading}
            isFetching={paydata.isFetching}
            error={paydata.error}
            primaryField="paydata_p_id"
            searchPlaceholder="Search pay data…"
            errorTitle="Could not load pay data"
            emptyTitle="No pay data"
            emptyDescription="No rows for the selected contract."
            maxBodyHeight={180}
            rightSlot={
              <>
                <Button size="small" variant="contained" color="primary" sx={actionBtnSx} onClick={handleWrite} disabled={disabled}>
                  {writeLabel}
                </Button>
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>Futures</Typography>
                  <TextField
                    size="small"
                    value={fixLevel == null ? '' : fmtNum2(fixLevel)}
                    InputProps={{ readOnly: true }}
                    inputProps={{ style: { fontSize: '0.7rem', textAlign: 'right', padding: '2px 6px' } }}
                    sx={{ width: 78 }}
                  />
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: '0.68rem', color: 'primary.main', fontWeight: 700, whiteSpace: 'nowrap' }}>Req. FX</Typography>
                  <TextField
                    size="small"
                    value={requestedFx}
                    onChange={(e) => onReqFxChange(e.target.value)}
                    onBlur={() => {
                      const n = Number(requestedFx.replace(/,/g, ''))
                      onReqFxChange(requestedFx.trim() === '' || !Number.isFinite(n) ? '' : fmtNum2(n))
                    }}
                    inputProps={{ style: { fontSize: '0.7rem', textAlign: 'right', padding: '2px 6px', fontWeight: 700 } }}
                    sx={{
                      width: 78,
                      // Field INPUT được → format khác (tint + viền primary) để phân biệt read-only.
                      '& .MuiInputBase-input': { color: 'primary.main' },
                      '& .MuiInputBase-root.MuiOutlinedInput-root': {
                        bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                      },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                    }}
                  />
                </Stack>
              </>
            }
          />
        </Box>
      </Box>

      {/* ---- INVOICE (full-width dưới) ---- */}
      <Box
        component="fieldset"
        sx={(theme) => ({
          minWidth: 0,
          m: 0,
          px: 0.75,
          pt: 0.5,
          pb: 0.5,
          border: '1px solid',
          borderColor: alpha(theme.palette.text.primary, 0.3),
          borderRadius: 1,
          display: 'flex',
          flexDirection: 'column',
        })}
      >
        <Box
          component="legend"
          sx={{
            px: 0.5,
            fontWeight: 700,
            fontSize: '0.72rem',
            letterSpacing: 0.3,
            color: 'text.primary',
            width: 'auto',
            mb: 0,
          }}
        >
          INVOICE
        </Box>
        <ProcurementDatasheet
          storageKey="procurement.contract-detail.invoice"
          columns={INVOICE_COLUMNS}
          rows={invoiceRows}
          isLoading={invoice.isLoading}
          isFetching={invoice.isFetching}
          error={invoice.error}
          primaryField="invoice_no"
          searchPlaceholder="Search invoices…"
          errorTitle="Could not load invoices"
          emptyTitle="No invoices"
          emptyDescription="No rows for the selected contract."
          maxBodyHeight={180}
          rightSlot={
            /* Access "For Sub" + "Write data Sub": Visible=No → không render */
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.68rem', whiteSpace: 'nowrap' }}>Invoices: For TLC</Typography>
              <Button size="small" variant="contained" color="secondary" sx={actionBtnSx} onClick={onWriteInvoice} disabled={disabled}>
                Write data
              </Button>
            </Stack>
          }
        />
      </Box>
    </Box>
  )
}
